/**
 * MissingOfficerCodeQueue — Domain 1.7 (Bill 2026-05-14).
 *
 * Lists active users with `MissingOfficerCode=true` and lets SysOps clear
 * the flag by entering an officer code that the server validates against
 * Core Director's OffCode catalog. On success, the user can immediately
 * resume admin/jXchange writes (the AdminAuthService 5-min role cache is
 * invalidated server-side as part of the resolve call).
 *
 * Backing endpoints:
 *   GET  /v3/admin/users/missing-officer-code             — list source
 *   POST /v3/admin/users/{userId}/resolve-officer-code    — clear flag
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, ShieldCheck, ShieldAlert, RefreshCw } from 'lucide-react';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { MissingOfficerCodeRow, CoreOperatorCatalogRow } from '@retail-ops/api-client';

interface ResolveDraft {
  user: MissingOfficerCodeRow;
  officerCode: string;
}

export function MissingOfficerCodeQueue(): JSX.Element {
  const api = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<MissingOfficerCodeRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<ResolveDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState<string | null>(null);
  const [refreshingCatalog, setRefreshingCatalog] = useState(false);
  const [catalogToast, setCatalogToast] = useState<string | null>(null);

  // v0.9.2 — typeahead picker over the cached SP catalog.
  const [catalog, setCatalog] = useState<ReadonlyArray<CoreOperatorCatalogRow> | null>(null);
  const loadCatalog = useCallback(async () => {
    try {
      const resp = await api.retail.admin.listCoreOperatorCatalog();
      if (resp.success && Array.isArray(resp.data)) setCatalog(resp.data);
    } catch { /* leave null — UI falls back to free-text */ }
  }, [api]);
  useEffect(() => { void loadCatalog(); }, [loadCatalog]);

  const onRefreshCatalog = useCallback(async (): Promise<void> => {
    setRefreshingCatalog(true);
    setCatalogToast(null);
    try {
      const resp = await api.retail.admin.refreshCoreOperatorCatalog();
      if (resp.success && resp.data) {
        const d = resp.data as Record<string, unknown>;
        // v0.3.7 — v0.9.8 in-memory refactor changed the response shape
        // (inserted/updated/retired → cached/duplicateGroups/issuesRaised/
        // issuesAutoResolved). Read defensively so old + new shapes both
        // render cleanly without "undefined" tokens leaking to the UI.
        const fetched = d.fetched ?? '?';
        const cached = d.cached ?? d.updated ?? '?';
        const dupes = d.duplicateGroups ?? 0;
        const raised = d.issuesRaised ?? 0;
        const resolved = d.issuesAutoResolved ?? 0;
        const users = typeof d.usersResolved === 'number' ? d.usersResolved : 0;
        const elapsed = d.elapsedMs ?? '?';
        // The list now resolves queued people it names by e-mail (2026-10-06) — say so plainly.
        setCatalogToast(
          `Catalog refreshed: ${cached} operators. ${users === 0 ? 'No one in the queue matched by e-mail.' : `${users} ${users === 1 ? 'person' : 'people'} matched by e-mail and cleared from the queue.`} Duplicate initials: ${dupes}; catalog issues raised ${raised}, cleared ${resolved}. (${elapsed} ms)`,
        );
        setQueueTick((t) => t + 1);
      } else {
        setCatalogToast(`Catalog refresh failed: ${resp.error ?? resp.code ?? 'unknown'}`);
      }
    } catch (e) {
      setCatalogToast(`Catalog refresh failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRefreshingCatalog(false);
      void loadCatalog(); // reload typeahead source after refresh
    }
  }, [api, loadCatalog]);

  const [queueTick, setQueueTick] = useState(0);
  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const resp = await api.retail.admin.listMissingOfficerCode();
      if (resp.success && Array.isArray(resp.data)) {
        setRows(resp.data);
      } else {
        setError('Failed to load queue.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload, queueTick]);

  const onResolve = useCallback(async (): Promise<void> => {
    if (!draft) return;
    const code = draft.officerCode.trim().toUpperCase();
    if (!code) {
      setSaveError('Operator initials are required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await api.retail.admin.resolveOfficerCode(draft.user.userId, {
        officerCode: code,
      });
      if (resp.success && resp.data) {
        setSavedToast(
          `Resolved ${draft.user.displayName} → ${resp.data.officerCode}`,
        );
        setDraft(null);
        await reload();
      } else {
        setSaveError(
          resp.error ??
            (resp.code === 'INVALID_OFFICER_CODE'
              ? `Operator initials ${code} are not in Core Director's OffCode catalog.`
              : `Resolve failed (${resp.code ?? 'unknown'}).`),
        );
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [api, draft, reload]);

  return (
    <>
      <h1>Missing Operator Initials queue</h1>
      <p className="page-intro">
        Active users whose operator initials haven&apos;t been validated. Until
        cleared here, their <code>/v3/admin/*</code> and <code>/jxrl/*</code>
        {' '}writes are rejected with HTTP 403 <code>OFFICER_CODE_MISSING</code>.
        Resolving validates the supplied initials against the cached PCB
        Core Operator Numbers catalog (synced from <code>/sites/SystemOps</code>).
        Click <b>Refresh catalog</b> if a new operator was just added to
        SharePoint.
      </p>

      <div className="catalog-refresh-bar">
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => void onRefreshCatalog()}
          disabled={refreshingCatalog}
        >
          <RefreshCw size={14} className={refreshingCatalog ? 'spin' : ''} />{' '}
          {refreshingCatalog ? 'Refreshing…' : 'Refresh catalog from SharePoint'}
        </button>
      </div>

      {catalogToast && (
        <div className="banner banner--info" role="status">
          {catalogToast}
        </div>
      )}

      {savedToast && (
        <div className="banner banner--ok" role="status">
          <ShieldCheck size={16} className="banner__icon" />
          {savedToast}
        </div>
      )}
      {error && (
        <div className="banner banner--err" role="status">
          {error}
        </div>
      )}

      {!rows && !error && (
        <div className="loading-inline">
          <Loader2 className="spin" size={16} /> Loading queue…
        </div>
      )}

      {rows && rows.length === 0 && (
        <div className="empty">
          <ShieldCheck size={32} className="empty__icon" />
          <p>No users currently flagged. Good shape!</p>
        </div>
      )}

      {rows && rows.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Display Name</th>
              <th>Current Operator Initials</th>
              <th>Branch</th>
              <th>Region</th>
              <th>Last Updated</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.userId}>
                <td><strong>{u.email}</strong></td>
                <td>{u.displayName}</td>
                <td>
                  {u.officerCode ? (
                    <code className="cell-mono">{u.officerCode}</code>
                  ) : (
                    <span className="cell-dim">—</span>
                  )}
                </td>
                <td>{u.branchCode ?? <span className="cell-dim">—</span>}</td>
                <td>{u.regionCode ?? <span className="cell-dim">—</span>}</td>
                <td className="cell-mono">
                  {u.updatedAt ? new Date(u.updatedAt).toLocaleString() : <span className="cell-dim">—</span>}
                </td>
                <td className="ta-right">
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    onClick={() =>
                      setDraft({ user: u, officerCode: u.officerCode ?? '' })
                    }
                  >
                    <ShieldAlert size={14} /> Resolve
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {draft && (
        <div className="modal-backdrop" onClick={() => (saving ? undefined : setDraft(null))}>
          <div
            className="modal modal--md"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="modal__header">
              <h3>Resolve operator initials — {draft.user.displayName}</h3>
              <button
                type="button"
                className="modal__close"
                aria-label="Close"
                onClick={() => (saving ? undefined : setDraft(null))}
              >
                ×
              </button>
            </header>
            <div className="modal__body">
              {saveError && (
                <div className="banner banner--err" role="status">
                  {saveError}
                </div>
              )}
              <p>
                Pick the operator from the cached PCB <i>Core Operator Numbers</i>
                catalog, or type their initials. Validation runs against the
                catalog (synced from SharePoint).
              </p>
              <CatalogTypeahead
                value={draft.officerCode}
                onChange={(v) => setDraft({ ...draft, officerCode: v })}
                catalog={catalog}
                displayName={draft.user.displayName}
              />
            </div>
            <footer className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setDraft(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void onResolve()}
                disabled={saving}
              >
                {saving ? 'Validating…' : 'Resolve'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

// v0.9.2 — Typeahead picker over the cached Core Operator Numbers catalog.
// Falls back to free-text input when the catalog hasn't loaded yet.
// Pre-suggests rows whose name fuzzy-matches the user's DisplayName so
// SysOps can usually pick with one click.
function CatalogTypeahead({
  value, onChange, catalog, displayName,
}: {
  value: string;
  onChange: (v: string) => void;
  catalog: ReadonlyArray<CoreOperatorCatalogRow> | null;
  displayName: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  const matches = useMemo(() => {
    if (!catalog) return [];
    const q = value.trim().toLowerCase();
    if (q.length === 0) {
      // No input yet — pre-suggest rows whose name fuzzy-matches the
      // flagged user's DisplayName. Cheap substring match on either
      // direction (Entra "Scott Bond" matches SP "Bond, Scott L").
      const tokens = displayName.toLowerCase().split(/\s+/).filter(t => t.length > 1);
      return catalog
        .filter((r) => tokens.some((t) => (r.operatorName ?? '').toLowerCase().includes(t)))
        .slice(0, 10);
    }
    return catalog
      .filter((r) =>
        r.operatorInitials.toLowerCase().includes(q) ||
        (r.operatorName ?? '').toLowerCase().includes(q) ||
        (r.networkId ?? '').toLowerCase().includes(q) ||
        (r.emailAddress ?? '').toLowerCase().includes(q),
      )
      .slice(0, 25);
  }, [catalog, value, displayName]);

  return (
    <div className="typeahead">
      <label className="form-field">
        <span className="form-field__label">
          Operator Initials <span aria-hidden="true" className="form-field__required">*</span>
        </span>
        <input
          type="text"
          className="form-input form-input--mono"
          autoFocus
          maxLength={8}
          value={value}
          onChange={(e) => { onChange(e.target.value.toUpperCase()); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          placeholder={catalog ? 'Type to search or pick below' : 'WW'}
        />
        <span className="form-field__hint">
          {catalog
            ? `${catalog.length} operators cached. Validation runs server-side on Resolve.`
            : 'Catalog not loaded — input validates server-side on Resolve.'}
        </span>
      </label>
      {open && matches.length > 0 && (
        <div className="typeahead__menu">
          {matches.map((m) => (
            <button
              key={m.operatorInitials}
              type="button"
              className="typeahead__option"
              onMouseDown={(e) => { e.preventDefault(); onChange(m.operatorInitials); setOpen(false); }}
            >
              <code className="cell-mono">{m.operatorInitials}</code>
              <span className="typeahead__desc">
                {m.operatorName ?? <i>(no name)</i>}
                {m.operatorTitle ? <span className="cell-dim"> · {m.operatorTitle}</span> : null}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
